// Lista produktów kolekcji i wyszukiwarki (@theme/facets): filtry, sortowanie, gęstość siatki, szuflada filtrów,
// historia (10 FR-10-11…37, 06 FR-06-32…37, ARCHITEKTURA §5.5). Znaczniki: snippets/facets.liquid, sections/main-search.liquid.
// Zmiana → pushState → SRA sekcji → podmiana fragmentów [data-fragment] + pola panelu aktualizowane w miejscu.
import { fetchSection, formatMoney, lockScroll, mq, onIdle, reducedMotion, standardEvent, swapFragments, unlockScroll } from '@theme/core';

const VIEW_KEY = 'product-grid-view-';
// Jak przejścia stron (layout): bez animacji na słabych urządzeniach i w przeglądarkach Meta.
const CALM_DEVICE =
  navigator.hardwareConcurrency <= 2 || navigator.deviceMemory <= 2 || /\b(FBAN|FBAV|FB_IAB|FBIOS|Instagram)\b/i.test(navigator.userAgent);

// „37.999,99”, „37 999.5”, „100” → grosze; ostatnia grupa ≤ 2 cyfr to część dziesiętna (jak w starym sklepie).
export function toCents(text) {
  const parts = String(text ?? '').split(/\D+/).filter(Boolean);
  if (!parts.length) return null;
  const decimal = parts.length > 1 && parts.at(-1).length <= 2;
  const whole = Number((decimal ? parts.slice(0, -1) : parts).join(''));
  return whole * 100 + (decimal ? Number(parts.at(-1).padEnd(2, '0')) : 0);
}

// Parametry formularza → adres listy: bez pustych pól (np. ceny) i bez numeru strony; fraza `q` zostaje.
export function filterParams(entries) {
  const params = new URLSearchParams();
  for (const [key, value] of entries) if (value !== '' || key === 'q') params.append(key, value);
  params.delete('page');
  return params;
}

const pageUrl = (href) => {
  const url = new URL(href, location.href);
  url.hash = '';
  return url;
};
const fieldKey = (field) => (field.type === 'checkbox' ? `${field.name}\n${field.value}` : field.name);

class FacetsResults extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.form = this.querySelector('[data-facets-form]');
    this.panel = this.querySelector('[data-facets-panel]');
    this.sidebar = this.querySelector('[data-facets-sidebar]');
    this.drawer = this.querySelector('[data-facets-drawer]');
    this.desktop = mq(750);
    this.target = pageUrl(location.href).href;

    this.addEventListener('change', (event) => this.onChange(event), { signal });
    this.addEventListener('click', (event) => this.onClick(event), { signal });
    this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.apply(this.formUrl());
    }, { signal });
    this.querySelector('[data-sort]')?.addEventListener('toggle', ({ target }) => {
      if (target.open) target.querySelector('[aria-selected="true"]')?.focus();
    }, { signal });
    this.drawer?.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.closeDrawer();
    }, { signal });
    this.drawer?.addEventListener('close', () => this.onDrawerClose(), { signal });
    this.desktop.addEventListener('change', () => this.onBreakpoint(), { signal });
    // BG-04: Wstecz/Dalej odświeża listę (przewinięcie przywraca core).
    addEventListener('popstate', () => this.onPopState(), { signal });

    this.classList.add('is-ready');
    this.sendViewEvent();
  }

  disconnectedCallback() {
    this.controller.abort();
    if (this.drawer) unlockScroll(this.drawer);
  }

  onChange({ target }) {
    if (target.matches('[data-density]')) return this.setView(target.value);
    if (target.matches('[data-sort-proxy]')) return this.sort(target.value);
    if (!this.form?.contains(target)) return;
    if (target.matches('[data-price-input]')) this.clampPrices(target.closest('[data-price]'));
    this.apply(this.formUrl());
  }

  onClick(event) {
    const { target } = event;
    const link = target.closest('a[data-facets-link], .results__empty a');
    if (link) {
      if (event.button || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      return this.apply(link.href);
    }
    const option = target.closest('[data-sort] [role="option"]');
    if (option) return this.selectSort(option);
    const opener = target.closest('[data-drawer-open]');
    if (opener) return this.openDrawer(opener);
    if (target === this.drawer || target.closest('[data-drawer-close]')) return this.closeDrawer();
    const more = target.closest('[data-show-more]');
    if (more) this.toggleMore(more);
  }

  onKeydown(event) {
    const { key, target } = event;
    if (target.matches('[data-price-input]')) {
      if (key === 'Enter') {
        event.preventDefault();
        this.clampPrices(target.closest('[data-price]'));
        this.apply(this.formUrl());
      } else if (key.length === 1 && !/[\d.,' ]/.test(key) && !event.ctrlKey && !event.metaKey) {
        event.preventDefault(); // FR-10-14: tylko cyfry i separatory
      }
    } else if (key === 'Enter' && target.matches('input[type="checkbox"]')) {
      event.preventDefault();
      target.click();
    } else if (target.closest('[data-sort] [role="listbox"]')) {
      this.onSortKey(event);
    }
  }

  // ---------- Adres i odświeżenie listy ----------

  formUrl() {
    const url = pageUrl(location.pathname);
    url.search = filterParams(new FormData(this.form)).toString();
    return url;
  }

  apply(href) {
    const url = pageUrl(href);
    if (url.href === this.target) return; // ten sam stan, np. change po Enter w polu ceny
    history.pushState({ facets: true }, '', url);
    const done = this.render(url);
    done.catch(() => {});
    this.sendUpdateEvent(done);
  }

  onPopState() {
    const url = pageUrl(location.href);
    if (url.pathname !== new URL(this.target).pathname || url.href === this.target) return;
    this.render(url).catch(() => {});
  }

  async render(url) {
    this.target = url.href;
    this.setAttribute('aria-busy', 'true');
    try {
      const doc = await fetchSection(this.dataset.sectionId, { url: url.href });
      const next = doc.querySelector('facets-results');
      if (!next) throw new Error('facets: brak listy w odpowiedzi sekcji');
      const update = () => this.update(next);
      if (this.canAnimate()) await document.startViewTransition({ update, types: ['product-grid'] }).updateCallbackDone;
      else update();
      this.removeAttribute('aria-busy');
      return Number(next.querySelector('[data-results-count]')?.dataset.resultsCount) || 0;
    } catch (error) {
      // Nowsze żądanie przerwało to; inny błąd — zwykła nawigacja pod ten sam adres.
      if (error.name !== 'AbortError') location.assign(url);
      throw error;
    }
  }

  update(next) {
    const active = this.contains(document.activeElement) ? document.activeElement : null;
    const focusKey = active?.closest('[data-focus-key]')?.dataset.focusKey;
    // Zmiana listy wartości podmienia cały blok filtrów — zwinięcia i „Pokaż więcej” kupującego zostają.
    const closed = [...this.querySelectorAll('[data-filter]:not([open])')].map((item) => item.dataset.filter);
    const expanded = [...this.querySelectorAll('[data-show-more][aria-expanded="true"]')].map((button) => button.getAttribute('aria-controls'));
    const swapped = swapFragments(next, this);
    if (swapped.some((node) => node.dataset.fragment === 'filters')) {
      for (const handle of closed) {
        const item = this.querySelector(`[data-filter="${CSS.escape(handle)}"]`);
        if (item) item.open = false;
      }
      for (const id of expanded) {
        const button = this.querySelector(`[data-show-more][aria-controls="${CSS.escape(id)}"]`);
        if (button) this.toggleMore(button, false);
      }
    }
    this.patchForm(next);
    const sort = next.querySelector('[data-sort] [aria-selected="true"]')?.dataset.value;
    if (sort) this.markSort(sort);
    this.setView(this.view(), { store: false, animate: false });
    if (active && !this.contains(document.activeElement)) {
      // Fokus wraca na przełączoną kontrolkę; panel lub „Zamknij” tylko, gdy jej już nie ma.
      if (focusKey) this.querySelector(`[data-focus-key="${CSS.escape(focusKey)}"]`)?.focus({ preventScroll: true });
      if (!this.contains(document.activeElement)) {
        (this.drawer?.open ? this.drawer.querySelector('[data-drawer-close]') : this.panel)?.focus({ preventScroll: true });
      }
    }
  }

  // Pola zostają (fokus, rozwinięcia, „Pokaż więcej”); stan bierzemy z odpowiedzi (ARCHITEKTURA §5.5).
  patchForm(next) {
    if (!this.form) return;
    const fields = new Map([...next.querySelectorAll('[data-facets-form] [name]')].map((field) => [fieldKey(field), field]));
    for (const field of this.form.querySelectorAll('[name]')) {
      const source = fields.get(fieldKey(field));
      if (!source) continue;
      if (field.type === 'checkbox') {
        field.checked = source.hasAttribute('checked');
        field.disabled = source.hasAttribute('disabled');
      } else if (field.matches('[data-price-input]')) {
        for (const name of ['placeholder', 'data-min', 'data-max']) field.setAttribute(name, source.getAttribute(name) ?? '');
        if (field !== document.activeElement) field.value = source.getAttribute('value') ?? '';
      } else if (field.tagName === 'SELECT') {
        field.value = source.querySelector('[selected]')?.value ?? field.value;
      }
    }
  }

  canAnimate() {
    return !this.drawer?.open && !CALM_DEVICE && !reducedMotion() && 'types' in (window.ViewTransition?.prototype ?? {});
  }

  // ---------- Cena (FR-10-14) ----------

  clampPrices(wrapper) {
    const [min, max] = wrapper?.querySelectorAll('[data-price-input]') ?? [];
    if (!max) return;
    for (const input of [min, max]) {
      const value = toCents(input.value);
      if (value === null) {
        input.value = '';
        continue;
      }
      const low = toCents(input.dataset.min) ?? 0;
      const high = toCents(input.dataset.max);
      if (value < low) input.value = formatMoney(low, { withCurrency: false });
      else if (high !== null && value > high) input.value = formatMoney(high, { withCurrency: false });
    }
    min.dataset.max = max.value || max.dataset.max;
    max.dataset.min = min.value || '0';
  }

  // ---------- Sortowanie: lista ≥750 (FR-10-19), select telefonu i szuflady ----------

  sort(value) {
    const select = this.form?.elements.sort_by;
    if (select) select.value = value;
    this.markSort(value);
    const url = this.form ? this.formUrl() : pageUrl(location.href);
    url.searchParams.set('sort_by', value);
    url.searchParams.delete('page');
    this.apply(url);
  }

  markSort(value) {
    for (const option of this.querySelectorAll('[data-sort] [role="option"]')) {
      const selected = option.dataset.value === value;
      option.setAttribute('aria-selected', selected);
      option.tabIndex = selected ? 0 : -1;
    }
    const proxy = this.querySelector('[data-sort-proxy]');
    if (proxy) proxy.value = value;
  }

  selectSort(option) {
    this.closeSort();
    this.sort(option.dataset.value);
  }

  closeSort() {
    const details = this.querySelector('[data-sort]');
    for (const option of details.querySelectorAll('[role="option"]')) {
      option.tabIndex = option.getAttribute('aria-selected') === 'true' ? 0 : -1;
    }
    details.open = false;
    details.querySelector('summary').focus();
  }

  onSortKey(event) {
    const options = [...this.querySelectorAll('[data-sort] [role="option"]')];
    const current = options.indexOf(event.target.closest('[role="option"]'));
    const step = { ArrowDown: 1, ArrowUp: -1 }[event.key];
    if (step) {
      const next = options[Math.min(Math.max(current + step, 0), options.length - 1)];
      for (const option of options) option.tabIndex = option === next ? 0 : -1;
      next.focus();
    } else if (event.key === 'Enter' || event.key === ' ') {
      if (current >= 0) this.selectSort(options[current]);
    } else if (event.key === 'Escape') {
      this.closeSort();
    } else {
      return;
    }
    event.preventDefault();
  }

  // ---------- Gęstość siatki (FR-10-20, 25, 27) ----------

  view() {
    const group = this.desktop.matches ? 'grid' : 'grid-mobile';
    return this.querySelector(`[data-density][name="${group}"]:checked`)?.value || 'default';
  }

  setView(value, { store = true, animate = true } = {}) {
    const grid = this.querySelector('[data-results-grid]');
    const desktop = this.desktop.matches;
    const apply = () => {
      grid?.classList.toggle('product-grid--zoom-out', desktop && value === 'zoom-out');
      grid?.classList.toggle('product-grid--single', !desktop && value === 'mobile-single');
    };
    if (animate && grid && this.canAnimate()) document.startViewTransition({ update: apply, types: ['product-grid'] });
    else apply();
    if (!store) return;
    try {
      sessionStorage.setItem(VIEW_KEY + (desktop ? 'desktop' : 'mobile'), value);
    } catch {}
  }

  // Po przejściu przez 750 px widok wraca do domyślnego (zapis zostaje); szuflada zamyka się na komputerze.
  onBreakpoint() {
    for (const radio of this.querySelectorAll('[data-density][value="default"]')) radio.checked = true;
    this.setView('default', { store: false, animate: false });
    if (this.desktop.matches && this.drawer?.open) this.drawer.close();
  }

  // ---------- Szuflada filtrów (FR-10-24, FR-95-4-06): ten sam panel co w sidebarze ----------

  openDrawer(trigger) {
    const { drawer } = this;
    if (!drawer || drawer.open) return;
    this.trigger = trigger;
    drawer.querySelector('[data-drawer-body]').append(this.panel);
    drawer.showModal();
    lockScroll(drawer);
    drawer.querySelector('[data-drawer-close]').focus();
  }

  async closeDrawer() {
    const { drawer } = this;
    if (!drawer?.open || drawer.classList.contains('is-closing')) return;
    drawer.classList.add('is-closing');
    await Promise.allSettled(drawer.getAnimations().map((animation) => animation.finished));
    drawer.close();
  }

  onDrawerClose() {
    this.drawer.classList.remove('is-closing');
    unlockScroll(this.drawer);
    this.sidebar.prepend(this.panel);
    if (!this.desktop.matches) this.trigger?.focus({ preventScroll: true });
  }

  // ---------- „Pokaż więcej” (FR-10-13): wysokość listy 300 ms ----------

  toggleMore(button, animate = true) {
    const list = document.getElementById(button.getAttribute('aria-controls'));
    if (!list) return;
    const expand = button.getAttribute('aria-expanded') !== 'true';
    const extra = list.querySelectorAll('[data-extra]');
    const show = (visible) => extra.forEach((item) => (item.hidden = !visible));
    const from = list.offsetHeight;
    show(expand);
    button.setAttribute('aria-expanded', expand);
    if (!animate || reducedMotion()) return;
    const to = list.offsetHeight;
    show(true); // przy zwijaniu pozycje znikają dopiero po animacji
    list.style.overflow = 'clip';
    const animation = list.animate({ height: [`${from}px`, `${to}px`] }, { duration: 300, easing: 'ease-in-out' });
    animation.finished.catch(() => {}).finally(() => {
      list.style.overflow = '';
      show(button.getAttribute('aria-expanded') === 'true');
    });
  }

  // ---------- Standard Events (KOL-EVT-01, D10) ----------

  sendViewEvent() {
    const script = this.closest('.shopify-section')?.querySelector('script[data-view-event]');
    if (!script) return;
    const send = () => onIdle(() => {
      try {
        const payload = JSON.parse(script.textContent);
        if (payload?.collection) standardEvent('shopify:collection:view', payload);
      } catch (error) {
        console.warn(error);
      }
    }, 5000);
    if (document.readyState === 'complete') send();
    else addEventListener('load', send, { once: true });
  }

  async sendUpdateEvent(done) {
    try {
      const events = await import('@shopify/events');
      const search = this.dataset.context === 'search';
      const EventClass = search ? events.SearchUpdateEvent : events.CollectionUpdateEvent;
      const params = new URL(location.href).searchParams;
      const productFilters = EventClass.parseProductFilters(params);
      const sortKey = EventClass.getSortKey(params);
      const deferred = EventClass.createPromise();
      deferred.promise.catch(() => {});
      const id = this.dataset.collectionId;
      const count = Number(this.querySelector('[data-results-count]')?.dataset.resultsCount) || 0;
      const payload = search
        ? { search: { query: params.get('q') ?? '', ...(productFilters && { productFilters }), sortKey } }
        : { collection: { id: /^\d+$/.test(id) ? id : null, handle: this.dataset.collectionHandle, productsCount: count }, ...(productFilters && { productFilters }), sortKey };
      this.dispatchEvent(new EventClass({ ...payload, promise: deferred.promise }));
      done.then((total) => deferred.resolve(search ? { totalCount: total } : { productsCount: total }), deferred.reject);
    } catch (error) {
      console.warn(error);
    }
  }
}

if (!customElements.get('facets-results')) customElements.define('facets-results', FacetsResults);

// Strona /search: Escape w pustym polu wraca do stanu bez frazy (FR-06-32). Przeglądarka czyści pole
// typu search pierwszym Escape, dlatego sprawdzamy po chwili.
document.addEventListener('keydown', (event) => {
  const input = event.target;
  if (event.key !== 'Escape' || !input.matches?.('[data-search-page-input]')) return;
  setTimeout(() => {
    if (input.value.trim() || !new URL(location.href).searchParams.get('q')?.trim()) return;
    input.value = '';
    input.form.submit();
  }, 100);
});
